import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { API } from "@/App";
import { resolveAssetUrl } from "@/lib/assetUrl";

export default function FoundingMembersPage() {
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [cap, setCap] = useState(100);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Founding Members — The Blk Community";
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/subscription/founding-members`);
        if (cancelled) return;
        setMembers(res.data.members || []);
        setTotal(res.data.total || 0);
        setCap(res.data.cap || 100);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-tbc-cream">
      <Navbar />

      <section className="bg-tbc-navy text-white py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-tbc-gold/15 border border-tbc-gold/30 text-tbc-gold text-xs font-medium tracking-wider uppercase mb-6">
            <Crown className="w-3.5 h-3.5" />
            Hall of Fame
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-6">
            Our Founding <span className="text-tbc-gold">Members</span>
          </h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto">
            The first 100 visionary businesses who believed in The Blk Community from day one.
            Their names live here forever.
          </p>
          <p className="mt-6 text-tbc-gold font-semibold" data-testid="founding-progress">
            {total} of {cap} spots claimed
          </p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <p className="text-center text-tbc-navy/50">Loading…</p>
          ) : members.length === 0 ? (
            <div className="text-center py-16 max-w-xl mx-auto">
              <Crown className="w-12 h-12 text-tbc-gold mx-auto mb-4" />
              <h2 className="font-heading text-2xl text-tbc-navy mb-2">All 100 spots are still open</h2>
              <p className="text-tbc-navy/70 mb-6">
                Be the very first Founding Member. Lock in $9.99/mo (or $99/yr) for life and earn a permanent spot here.
              </p>
              <Link
                to="/pricing"
                data-testid="founding-empty-cta"
                className="inline-flex items-center gap-2 rounded-full bg-tbc-gold text-tbc-navy px-6 py-3 font-semibold hover:bg-tbc-gold/90"
              >
                Claim Founding Spot #1
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {members.map((m) => (
                <Link
                  key={m.business_id}
                  to={`/business/${m.business_id}`}
                  data-testid={`founding-member-${m.founding_member_number}`}
                  className="bg-white rounded-2xl border border-tbc-navy/10 p-5 flex items-center gap-4 hover:border-tbc-gold/60 hover:shadow-lg transition"
                >
                  <div className="relative flex-shrink-0">
                    {m.image_url ? (
                      <img src={resolveAssetUrl(m.image_url)} alt={m.name} className="w-16 h-16 rounded-xl object-cover ring-1 ring-tbc-gold/30" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-tbc-navy flex items-center justify-center text-tbc-gold font-heading text-xl">
                        {m.name?.[0] || "?"}
                      </div>
                    )}
                    <span className="absolute -bottom-1 -right-1 bg-tbc-gold text-tbc-navy text-[10px] font-bold rounded-full w-6 h-6 flex items-center justify-center ring-2 ring-white">
                      #{m.founding_member_number}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-base text-tbc-navy truncate">{m.name}</p>
                    <p className="text-xs text-tbc-navy/60 truncate">{m.category}</p>
                    {(m.city || m.state) && (
                      <p className="text-xs text-tbc-navy/50 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" />
                        {[m.city, m.state].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
